import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import moment from 'moment';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const REPORT_TYPES = [
  { value: 'DAILY', label: 'Daily Call QA' },
  { value: 'WEEKLY', label: 'Weekly Call QA' },
  { value: 'BY_AGENT', label: 'By Agent' },
  { value: 'BY_CAMPAIGN', label: 'By Campaign / Campaign Dashboard' },
  { value: 'CRITICAL_FAILURES', label: 'Critical Failures' },
  { value: 'COMPLAINT_CALLS', label: 'Complaint Calls' },
  { value: 'COACHING_OPPORTUNITIES', label: 'Coaching Opportunities' },
  { value: 'RISK_PHRASE', label: 'Risk Phrase Report' },
  { value: 'UNSCORED', label: 'Unscored Calls' },
  { value: 'UNLINKED', label: 'Unlinked Calls' },
  { value: 'REVIEW_QUEUE', label: 'Human Review Queue' },
];

type Col = { key: string; label: string; render?: (row: any) => React.ReactNode; align?: 'right' };

const fmtDate = (v: any) => (v ? moment(v).format('MMM D, YYYY h:mm A') : '—');
const fmtDay = (v: any) => (v ? moment(v).format('MMM D, YYYY') : '—');

const callLink = (id: any, label: React.ReactNode, seekMs?: any) => (
  id ? (
    <RouterLink
      to={`/call-qa/calls/${id}${seekMs ? `?seek_ms=${seekMs}` : ''}`}
      style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 600 }}
    >
      {label}
    </RouterLink>
  ) : '—'
);

const severityChip = (s: any) => {
  const sev = String(s || '').toLowerCase();
  const color = sev === 'critical' ? 'error' : sev === 'serious' ? 'warning' : 'default';
  return <Chip size="small" color={color as any} variant={color === 'default' ? 'outlined' : 'filled'} label={sev || '—'} />;
};

const agentCell = (row: any) => row.agent || row.employee_name || (row.employee_id ? 'Unlinked' : '—');
const openCall = (row: any) => callLink(row.call_id, 'Open call →');

const qaPct = (row: any) => (row.average_qa == null
  ? <Chip size="small" variant="outlined" label="n/a" />
  : <>{row.average_qa}%{row.sample_insufficient ? <Chip size="small" color="warning" variant="outlined" label="low sample" sx={{ ml: 0.5 }} /> : null}</>);

const COLUMNS: Record<string, Col[]> = {
  BY_AGENT: [
    { key: 'agent', label: 'Agent', render: agentCell },
    { key: 'calls', label: 'Calls', align: 'right' },
    { key: 'scored_calls', label: 'Scored', align: 'right' },
    { key: 'average_qa', label: 'Avg QA', render: qaPct, align: 'right' },
    { key: 'critical_failures', label: 'Critical', align: 'right' },
    { key: 'needing_review', label: 'To review', align: 'right' },
  ],
  BY_CAMPAIGN: [
    { key: 'campaign_name', label: 'Campaign' },
    { key: 'total_calls', label: 'Calls', align: 'right' },
    { key: 'scored_calls', label: 'Scored', align: 'right' },
    { key: 'average_qa', label: 'Avg QA', render: qaPct, align: 'right' },
    { key: 'critical_failures', label: 'Critical', align: 'right' },
    { key: 'complaints', label: 'Complaints', align: 'right' },
    { key: 'risk_flags', label: 'Risk flags', align: 'right' },
  ],
  CRITICAL_FAILURES: [
    { key: 'when', label: 'When', render: (r) => fmtDate(r.when) },
    { key: 'agent', label: 'Agent', render: agentCell },
    { key: 'customer', label: 'Customer' },
    { key: 'final_overall_score', label: 'Score', render: (r) => (r.final_overall_score == null ? '—' : `${r.final_overall_score}%`), align: 'right' },
    { key: 'critical_failure_count', label: 'Critical', align: 'right' },
    { key: 'open', label: '', render: openCall },
  ],
  COMPLAINT_CALLS: [
    { key: 'complaint_received_at', label: 'Received', render: (r) => fmtDay(r.complaint_received_at) },
    { key: 'agent', label: 'Agent', render: agentCell },
    { key: 'customer_name', label: 'Customer', render: (r) => r.customer_name || r.customer || '—' },
    { key: 'campaign', label: 'Campaign', render: (r) => r.campaign || '—' },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" variant="outlined" label={r.status || '—'} /> },
    { key: 'finding', label: 'Finding' },
  ],
  COACHING_OPPORTUNITIES: [
    { key: 'agent', label: 'Agent', render: agentCell },
    { key: 'topic', label: 'Topic' },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" variant="outlined" label={r.status || '—'} /> },
    { key: 'due_at', label: 'Due', render: (r) => fmtDay(r.due_at) },
  ],
  RISK_PHRASE: [
    { key: 'when', label: 'When', render: (r) => fmtDate(r.when || r.created_at) },
    { key: 'agent', label: 'Agent', render: agentCell },
    { key: 'rule', label: 'Rule' },
    { key: 'phrase', label: 'Phrase', render: (r) => (r.phrase ? <em>“{r.phrase}”</em> : '—') },
    { key: 'severity', label: 'Severity', render: (r) => severityChip(r.severity) },
    { key: 'open', label: '', render: (r) => callLink(r.call_id, 'Open →', r.start_ms) },
  ],
  UNSCORED: [
    { key: 'when', label: 'When', render: (r) => fmtDate(r.when || r.started_at) },
    { key: 'agent', label: 'Agent', render: agentCell },
    { key: 'campaign', label: 'Campaign', render: (r) => r.campaign || '—' },
    { key: 'qa_status', label: 'QA status', render: (r) => <Chip size="small" variant="outlined" label={r.qa_status || '—'} /> },
    { key: 'open', label: '', render: openCall },
  ],
  UNLINKED: [
    { key: 'when', label: 'When', render: (r) => fmtDate(r.when || r.started_at) },
    { key: 'customer', label: 'Caller', render: (r) => r.customer || r.customer_phone || '—' },
    { key: 'recording_source', label: 'Source' },
    { key: 'match_status', label: 'Match', render: (r) => <Chip size="small" variant="outlined" label={r.match_status || 'UNMATCHED'} /> },
    { key: 'open', label: '', render: openCall },
  ],
};

const SUMMARY_TYPES = ['DAILY', 'WEEKLY', 'SUMMARY'];

const CallQaReportsPage: React.FC = () => {
  const { user } = useAuth();
  const canRead = (user?.permissions || []).includes('call_qa.read');
  const [reportType, setReportType] = useState('DAILY');

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['callQaReport', reportType],
    queryFn: () => apiClient.callQa.getReport(reportType),
    enabled: canRead,
  });

  const reviewQuery = useQuery({
    queryKey: ['callQaReviewQueue'],
    queryFn: () => apiClient.callQa.getReviewQueue(),
    enabled: canRead && reportType === 'REVIEW_QUEUE',
  });

  if (!canRead) {
    return <Alert severity="warning">Call QA reports require call_qa.read.</Alert>;
  }

  const payload = data?.data?.data;
  const review = reviewQuery.data?.data?.data;
  const cols = COLUMNS[reportType];
  const rows: any[] = Array.isArray(payload?.rows) ? payload.rows : [];
  const busy = isLoading || isFetching;

  const renderTable = (columns: Col[], data: any[]) => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.key} align={c.align} sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>{c.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.slice(0, 200).map((row, idx) => (
            <TableRow key={idx} hover>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align}>
                  {c.render ? c.render(row) : (row[c.key] ?? '—')}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Call QA Reports</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sample sizes are shown with averages. Tiny samples (&lt;5 scored calls) are flagged as insufficient.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>Report</InputLabel>
          <Select label="Report" value={reportType} onChange={(e) => setReportType(String(e.target.value))}>
            {REPORT_TYPES.map((r) => (
              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={() => refetch()} disabled={isFetching}>Refresh</Button>
        {busy && <CircularProgress size={20} />}
      </Box>

      {error && <Alert severity="error">Failed to load report.</Alert>}

      {/* Headline for summary reports */}
      {payload && !busy && SUMMARY_TYPES.includes(reportType) && (
        <Box>
          {payload.average_qa != null && (
            <Alert severity={payload.sample_insufficient ? 'warning' : 'info'} sx={{ mb: 2 }}>
              Average QA: {payload.average_qa}% (n={payload.sample_size}
              {payload.sample_insufficient ? ' — sample insufficient' : ''})
            </Alert>
          )}
          <Box display="flex" gap={1.5} flexWrap="wrap" mb={1}>
            {[
              ['Calls', payload.total_calls],
              ['Scored', payload.scored_calls],
              ['Critical', payload.critical_failures],
              ['Complaints', payload.complaints],
              ['Coaching', payload.coaching_items],
              ['Risk flags', payload.risk_flags],
            ].map(([label, n]) => (
              <Paper key={String(label)} variant="outlined" sx={{ px: 2, py: 1, minWidth: 92 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>{n ?? 0}</Typography>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
              </Paper>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {fmtDay(payload.from)} – {fmtDay(payload.to)}
          </Typography>
        </Box>
      )}

      {/* Tabular reports */}
      {payload && !busy && cols && (
        rows.length > 0
          ? renderTable(cols, rows)
          : <Alert severity="info">No calls match this report for the current period.</Alert>
      )}

      {/* Review queue (own shape) */}
      {reportType === 'REVIEW_QUEUE' && review && !busy && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Review queue: {review.review_queue_count} · Auto-scored: {review.auto_scored_count}
            · Random sample {review.random_percent}% · Low-score threshold {review.low_score_threshold}
          </Alert>
          {(review.rows || []).length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Call</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Reasons</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }} align="right">Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(review.rows || []).slice(0, 200).map((row: any) => (
                    <TableRow key={row.call_id} hover>
                      <TableCell>{callLink(row.call_id, 'Open call →')}</TableCell>
                      <TableCell>{(row.reasons || []).join(', ')}</TableCell>
                      <TableCell align="right">{row.score ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : <Alert severity="info">The review queue is empty.</Alert>}
        </Box>
      )}

      {payload?.ranking_label && !busy && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {payload.ranking_label}
        </Typography>
      )}
      {payload?.note && !busy && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {payload.note}
        </Typography>
      )}
    </Box>
  );
};

export default CallQaReportsPage;
